import React, { useState } from 'react';
import { ChevronRight, ArrowUpRight, Check, X, Clock, ClipboardCheck } from 'lucide-react';
import type { AmsAuditApproval, SectionApproval } from '../../../services/amsAuditApprovalService';
import type { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import { describeAuditForApprover } from '../utils/computeAuditPendingSummary';
import { getAuditBucket } from '../utils/auditApprovalStatus';

interface Props {
  approval: AmsAuditApproval;
  audit: AmsProjectAudit | null;
  onOpen: (approval: AmsAuditApproval, audit: AmsProjectAudit | null) => void;
}

const BUCKET_PILL: Record<'pending' | 'rejected' | 'approved', { label: string; cls: string; dot: string }> = {
  pending: { label: 'Pending Review', cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  rejected: { label: 'Rejected', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  approved: { label: 'Approved to Start', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
};

const sectionBadge = (status: SectionApproval['status']) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-[3px] text-[10.5px] font-bold text-emerald-700">
        <Check className="h-3 w-3" />
        Approved
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-[3px] text-[10.5px] font-bold text-red-700">
        <X className="h-3 w-3" />
        Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-[3px] text-[10.5px] font-bold text-amber-700">
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
};

const auditTypeName = (audit: AmsProjectAudit | null): string =>
  !audit
    ? 'Unknown Audit'
    : typeof audit.auditTypeId === 'string'
      ? audit.auditTypeId
      : audit.auditTypeId?.name || 'Unknown Audit';

const AmsAuditApprovalRow: React.FC<Props> = ({ approval, audit, onOpen }) => {
  const [open, setOpen] = useState(false);
  const bucket = getAuditBucket(approval) ?? 'pending';
  const pill = BUCKET_PILL[bucket];
  const name = auditTypeName(audit);
  const subline = describeAuditForApprover(approval);

  // Checkpoint tally (project + asset combined)
  const cps = [
    ...approval.checkpointApprovals.projectCheckpoints,
    ...approval.checkpointApprovals.assetCheckpoints,
  ];
  const cpTotal = cps.length;
  const cpReviewed = cps.filter((c) => c.status === 'approved' || c.status === 'rejected').length;
  const cpPending = cps.filter((c) => c.status === 'pending').length;
  const cpRejected = cps.filter((c) => c.status === 'rejected').length;

  // Sections shown: Audit Type + Auditors only (Assets & Equipment intentionally omitted)
  const shownSections: Array<{ name: string; status: SectionApproval['status'] }> = [
    { name: 'Audit Type', status: approval.sections.auditType.status },
    { name: 'Auditors', status: approval.sections.auditors.status },
  ];
  const secReviewed = shownSections.filter((s) => s.status === 'approved' || s.status === 'rejected').length;
  const revTotal = shownSections.length + cpTotal;
  const rev = secReviewed + cpReviewed;
  const pct = revTotal > 0 ? Math.round((rev / revTotal) * 100) : 100;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen(approval, audit);
  };

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="flex cursor-pointer items-center gap-2.5 py-2.5 transition-colors hover:bg-gray-50"
      >
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${pill.dot}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-gray-900">{name}</p>
          <p
            className={`mt-0.5 text-[11px] ${
              bucket === 'rejected'
                ? 'font-semibold text-red-700'
                : bucket === 'pending'
                  ? 'font-semibold text-amber-700'
                  : 'text-gray-500'
            }`}
          >
            {subline}
          </p>
        </div>
        <span className={`shrink-0 whitespace-nowrap rounded-full border px-2 py-[2px] text-[10px] font-bold ${pill.cls}`}>
          {pill.label}
        </span>
        <button
          type="button"
          onClick={handleOpen}
          title="Open in review drawer"
          aria-label="Open in review drawer"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-colors hover:border-blue-600 hover:bg-blue-600 hover:text-white"
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90 text-blue-600' : ''}`}
        />
      </div>

      {open && (
        <div className="pb-3.5 pt-1">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="text-[11px] font-bold tabular-nums text-gray-600">
              {rev}/{revTotal}
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full ${
                  pct === 100
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-bold tabular-nums text-gray-600">{pct}%</span>
          </div>

          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">Sections</p>
          {shownSections.map((s) => (
            <div
              key={s.name}
              className="mb-1.5 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5"
            >
              <span className="grid h-6 w-6 place-items-center rounded-md bg-gray-50 text-gray-400">
                <ClipboardCheck className="h-3.5 w-3.5" />
              </span>
              <span className="flex-1 text-[12.5px] font-semibold text-gray-800">{s.name}</span>
              {sectionBadge(s.status)}
            </div>
          ))}

          {cpTotal > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
              <span className="min-w-[120px] flex-1 text-[12px] font-semibold text-gray-700">
                Checkpoints — {cpReviewed}/{cpTotal} reviewed
              </span>
              {cpPending > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-[3px] text-[10.5px] font-bold text-amber-700">
                  <Clock className="h-3 w-3" />
                  {cpPending} pending
                </span>
              ) : cpRejected > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-[3px] text-[10.5px] font-bold text-red-700">
                  <X className="h-3 w-3" />
                  {cpRejected} rejected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-[3px] text-[10.5px] font-bold text-emerald-700">
                  <Check className="h-3 w-3" />
                  All approved
                </span>
              )}
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={handleOpen}
              aria-label="Open in review drawer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3.5 py-1.5 text-[11.5px] font-bold text-white transition-[filter] hover:brightness-110"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              Open in review drawer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmsAuditApprovalRow;
